// src/components/UnifiedSignIn.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import {
  loginWithEmail,
  registerWithEmail,
  logout,
} from '../AuthService';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';
import {
  getDoc,
  setDoc,
  serverTimestamp,
  doc,
} from 'firebase/firestore';

// Use the phone input library
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { userDoc } from '../utils/firestoreRefs'; // (removed the COUNTRY_CODES import)
import { db } from '../firebase';

function UnifiedSignIn() {
  const navigate = useNavigate();

  // Tab states
  const [activeTab, setActiveTab] = useState('email'); // 'email' | 'phone'
  const [authMode,  setAuthMode]  = useState('login'); // 'login' | 'register'

  // Email auth fields
  const [firstName,    setFirstName]    = useState('');
  const [email,        setEmail]        = useState('');
  const [phoneForEmail,setPhoneForEmail]= useState(''); // optional phone for email-based reg
  const [password,     setPassword]     = useState('');

  // Phone auth fields
  const [phoneValue, setPhoneValue]           = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);

  // Misc
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Track auth state
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => setCurrentUser(user));
    return () => unsub();
  }, []);

  // Invisible reCAPTCHA setup
  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        'recaptcha-container',
        {
          size: 'invisible',
          callback: () => console.log('✅ reCAPTCHA verified'),
          'expired-callback': () => {
            console.warn('⚠️ reCAPTCHA expired, resetting...');
            window.recaptchaVerifier.clear();
            setupRecaptcha();
          },
        },
        auth
      );
      window.recaptchaVerifier.render();
    }
  };

  // ============================
  // SMART POST-LOGIN ROUTING
  // ============================
  const determinePostLoginRoute = async (user) => {
    try {
      const uid = user.uid;
      const userEmail = user.email;

      // Check if admin (hardcoded emails)
      const adminEmails = [
        'matthew@backpass.co',
        'mgriff7@gmail.com',
        'mhgbackpass@gmail.com',
        'mhgcommercial@gmail.com'
      ];
      const isAdmin = adminEmails.includes(userEmail);

      // Check if user doc has isAdmin flag
      let isAdminFromDoc = false;
      try {
        const userDocSnap = await getDoc(doc(db, 'users', uid));
        if (userDocSnap.exists() && userDocSnap.data().isAdmin === true) {
          isAdminFromDoc = true;
        }
      } catch (error) {
        console.error('Error checking user admin status:', error);
      }

      const finalIsAdmin = isAdmin || isAdminFromDoc;

      // Check if creator
      let isCreator = false;
      try {
        const userDocSnap = await getDoc(doc(db, 'users', uid));
        if (userDocSnap.exists() && userDocSnap.data().isCreator === true) {
          isCreator = true;
        }
      } catch (error) {
        console.error('Error checking creator status:', error);
      }

      // Route based on roles
      if (finalIsAdmin && isCreator) {
        // Both admin and creator -> show choice
        return '/role-selection';
      } else if (finalIsAdmin) {
        // Admin only
        return '/admin';
      } else if (isCreator) {
        // Creator only
        return '/creator-dashboard';
      } else {
        // Regular user -> mobile redirect
        return '/mobile-redirect';
      }
    } catch (error) {
      console.error('Error determining post-login route:', error);
      return '/mobile-redirect'; // Default fallback
    }
  };

  // ============================
  // EMAIL AUTH SUBMIT
  // ============================
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (currentUser) {
        setError('Already signed in. Log out first to sign in again.');
        return;
      }

      if (authMode === 'register') {
        // Register
        const userCred = await registerWithEmail(email, password, {
          firstName,
          phoneNumber: phoneForEmail,
        });
        alert('We sent a verification link to your email. Please verify before logging in.');

        const uid        = userCred.user.uid;
        const emailLower = email.toLowerCase();
        const docRef     = userDoc(uid);
        const docSnap    = await getDoc(docRef);

        if (!docSnap.exists()) {
          await setDoc(docRef, {
            firstName,
            email: emailLower,
            phoneNumber: phoneForEmail,
            createdAt: serverTimestamp(),
          });
        }

        // Force logout so they re-auth after verifying
        await logout();

        // On to user-info
        navigate('/user-info', {
          state: { firstName, email: emailLower, phone: phoneForEmail },
        });

      } else {
        // Login
        const userCred = await loginWithEmail(email, password);
        if (!userCred.user.emailVerified) {
          await logout();
          throw new Error('Your email is not verified. Please check your inbox.');
        }

        // Firestore doc
        const uid = userCred.user.uid;
        const docSnap = await getDoc(userDoc(uid));
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(`Logged in as: ${data.firstName} at ${data.email}`);
        } else {
          console.log(`Logged in as: (no profile) at ${userCred.user.email}`);
        }

        // Smart routing based on user role
        const route = await determinePostLoginRoute(userCred.user);
        navigate(route);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================
  // PHONE AUTH
  // ============================
  const handleSendCode = async () => {
    setError('');
    setLoading(true);
    try {
      setupRecaptcha();

      if (!phoneValue || phoneValue.length < 5) {
        setError('Please enter a valid phone number.');
        return;
      }

      const appVerifier = window.recaptchaVerifier;
      const result = await signInWithPhoneNumber(auth, phoneValue, appVerifier);
      setConfirmationResult(result);
      alert('📲 SMS code sent!');
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);
    try {
      if (!confirmationResult) {
        setError('No confirmation result. Please request a code first.');
        return;
      }

      const cred = await confirmationResult.confirm(verificationCode);
      console.log(`Signed in with phone: ${cred.user.phoneNumber}`);

      // Smart routing based on user role
      const route = await determinePostLoginRoute(cred.user);
      navigate(route);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================
  // LOGOUT
  // ============================
  const handleLogout = async () => {
    setError('');
    setLoading(true);
    try {
      await logout();
      alert('Logged out successfully!');
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================
  // RENDER
  // ============================
  return (
    <div style={{ padding: '20px' }}>
      <h2>Sign In (Email or Phone)</h2>

      {currentUser && (
        <div style={{ marginBottom: '1em' }}>
          <button onClick={handleLogout} disabled={loading}>
            Logout
          </button>
        </div>
      )}

      {/* Tab toggles */}
      <div style={{ marginBottom: '1em' }}>
        <button onClick={() => setActiveTab('email')} disabled={activeTab === 'email'}>
          Email
        </button>
        <button onClick={() => setActiveTab('phone')} disabled={activeTab === 'phone'}>
          Phone
        </button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>Loading...</p>}

      {/* ========== EMAIL SECTION ========== */}
      {activeTab === 'email' && (
        <div>
          {/* register or login? */}
          <div style={{ marginBottom: '1em' }}>
            <button onClick={() => setAuthMode('login')} disabled={authMode === 'login'}>
              Login
            </button>
            <button onClick={() => setAuthMode('register')} disabled={authMode === 'register'}>
              Register
            </button>
          </div>

          <form onSubmit={handleEmailSubmit}>
            {authMode === 'register' && (
              <>
                <div>
                  <label>First Name: </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label>Phone (optional): </label>
                  <input
                    type="tel"
                    value={phoneForEmail}
                    onChange={(e) => setPhoneForEmail(e.target.value)}
                    placeholder="e.g. 555-555-1234"
                  />
                </div>
              </>
            )}

            <div>
              <label>Email: </label>
              <input
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label>Password: </label>
              <input
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button type="submit" disabled={loading}>
              {authMode === 'register' ? 'Register' : 'Login'}
            </button>
          </form>
        </div>
      )}

      {/* ========== PHONE SECTION ========== */}
      {activeTab === 'phone' && (
        <div>
          {/* The container for invisible reCAPTCHA */}
          <div id="recaptcha-container" style={{ marginBottom: '1rem' }} />

          <div style={{ marginTop: '1rem' }}>
            <label>Phone Number:</label>
            <div style={{ margin: '8px 0' }}>
              {/* Single phone input from react-phone-number-input */}
              <PhoneInput
                defaultCountry="US"
                placeholder="Enter phone number"
                value={phoneValue}
                onChange={setPhoneValue}
              />
            </div>

            {!confirmationResult && (
              <button onClick={handleSendCode} disabled={loading}>
                Send Code
              </button>
            )}

            {confirmationResult && (
              <div style={{ marginTop: '1rem' }}>
                <label>Verification Code: </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  required
                />
                <button onClick={handleVerifyCode} disabled={loading}>
                  Verify Code
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Internal Use Notice */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <p style={{
          margin: 0,
          fontSize: '0.95rem',
          lineHeight: '1.6'
        }}>
          <strong>⚠️ Internal Use Only:</strong> This log in is for internal use only! If you are not an admin or route creator there isn't anything for you here. If you are a route creator and you cannot log in, contact <a href="mailto:matthew@backpass.co" style={{ color: '#856404', textDecoration: 'underline' }}>matthew@backpass.co</a>. Creating a new account will not automatically bestow creator status.
        </p>
      </div>
    </div>
  );
}

export default UnifiedSignIn;
